import { useQuery, useQueryClient } from '@tanstack/react-query';

import { TableView } from '@components/table/types';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';

import {
  CreateTableViewData,
  TableType,
  TableViewApi,
  TableViewSettingsResponse,
  UpdateTableViewData,
  UpdateTableViewPreferenceData,
  createTableView,
  deleteTableView,
  loadTableViewSettings,
  updateTableView,
  updateTableViewPreferences,
} from './requests';

type TableViewQueryParams = {
  tableType: TableType;
  tableObjectShortId?: string;
};

const getSettingsQueryKey = ({ tableType, tableObjectShortId }: TableViewQueryParams) =>
  ['table-view-settings', tableType, tableObjectShortId].filter(Boolean);

export const apiViewToTableView = <View extends TableView = TableView>(
  apiView: TableViewApi
): View => {
  return {
    ...apiView.config,
    id: apiView.shortId,
    name: apiView.name,
    isCollaborative: apiView.isCollaborative,
    isOwn: apiView.isOwn,
  } as View;
};

export const tableViewToConfig = (view: TableView): Record<string, any> => {
  const { id, name, isCollaborative, isOwn, favoriteItem, ...config } = view;
  return config;
};

export const useCreateTableView = (params: TableViewQueryParams) => {
  const queryClient = useQueryClient();
  const settingsKey = getSettingsQueryKey(params);

  return useMutation({
    mutationFn: (data: CreateTableViewData) => createTableView(data),
    onSuccess: (newView) => {
      const old = queryClient.getQueryData<TableViewSettingsResponse>(settingsKey);
      const viewOrder = [...(old?.viewOrder ?? []), newView.shortId];
      queryClient.setQueryData<TableViewSettingsResponse>(settingsKey, () => ({
        views: [...(old?.views ?? []), newView],
        selectedView: newView.shortId,
        viewOrder,
      }));
      updateTableViewPreferences(params, {
        selectedView: newView.shortId,
        viewOrder,
      });
    },
  });
};

export const useUpdateTableView = (params: TableViewQueryParams) => {
  const queryClient = useQueryClient();
  const settingsKey = getSettingsQueryKey(params);

  return useMutation({
    mutationFn: ({ shortId, data }: { shortId: string; data: UpdateTableViewData }) =>
      updateTableView(shortId, data),
    onMutate: async ({ shortId, data }) => {
      await queryClient.cancelQueries({ queryKey: settingsKey });
      const previous = queryClient.getQueryData<TableViewSettingsResponse>(settingsKey);

      queryClient.setQueryData<TableViewSettingsResponse>(settingsKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          views: old.views.map((view) => {
            if (view.shortId !== shortId) return view;
            return {
              ...view,
              ...(data.name !== undefined && { name: data.name }),
              ...(data.isCollaborative !== undefined && { isCollaborative: data.isCollaborative }),
              ...(data.config !== undefined && { config: data.config }),
            };
          }),
        };
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(settingsKey, context.previous);
      }
    },
  });
};

export const useDeleteTableView = (params: TableViewQueryParams) => {
  const queryClient = useQueryClient();
  const settingsKey = getSettingsQueryKey(params);

  return useMutation({
    mutationFn: (shortId: string) => deleteTableView(shortId),
    onMutate: async (shortId) => {
      await queryClient.cancelQueries({ queryKey: settingsKey });
      const previous = queryClient.getQueryData<TableViewSettingsResponse>(settingsKey);

      queryClient.setQueryData<TableViewSettingsResponse>(settingsKey, (old) => {
        if (!old) return old;
        const remaining = old.views.filter((view) => view.shortId !== shortId);
        const wasSelected = old.selectedView === shortId;
        return {
          views: remaining,
          selectedView: wasSelected ? remaining[0]?.shortId ?? null : old.selectedView,
          viewOrder: old.viewOrder.filter((id) => id !== shortId),
        };
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(settingsKey, context.previous);
      }
    },
  });
};

export const useUpdateTableViewPreferences = (params: TableViewQueryParams) => {
  const queryClient = useQueryClient();
  const settingsKey = getSettingsQueryKey(params);

  return useMutation({
    mutationFn: (data: UpdateTableViewPreferenceData) => updateTableViewPreferences(params, data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: settingsKey });
      const previous = queryClient.getQueryData<TableViewSettingsResponse>(settingsKey);

      queryClient.setQueryData<TableViewSettingsResponse>(settingsKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          selectedView: data.selectedView ?? old.selectedView,
          viewOrder: data.viewOrder ?? old.viewOrder,
        };
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(settingsKey, context.previous);
      }
    },
  });
};

export const useTableViewSettings = (params: TableViewQueryParams) => {
  const query = useQuery({
    queryKey: getSettingsQueryKey(params),
    queryFn: () => loadTableViewSettings(params),
    select: (data): { views: TableView[]; selectedViewId?: string } => {
      const viewMap = new Map(data.views.map((view) => [view.shortId, view]));
      const ordered: TableView[] = [];

      // First add views in the user's preferred order
      if (data.viewOrder) {
        for (const shortId of data.viewOrder) {
          const apiView = viewMap.get(shortId);
          if (apiView) {
            ordered.push(apiViewToTableView(apiView));
            viewMap.delete(shortId);
          }
        }
      }

      // Then append any views not in the order (e.g., newly shared collaborative views)
      for (const apiView of viewMap.values()) {
        ordered.push(apiViewToTableView(apiView));
      }

      return {
        views: ordered,
        selectedViewId: data.selectedView ?? undefined,
      };
    },
  });

  return {
    views: query.data?.views,
    selectedViewId: query.data?.selectedViewId,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
